/** Row of compliance/security indicators; `variant='dark'` for dark sections. */

import React from 'react';
import { Shield, Lock, ShieldCheck, KeyRound } from 'lucide-react';
import { cn } from '@/lib/utils/utils';

const BADGES = [
  { icon: Shield, label: 'FERPA Ready' },
  { icon: ShieldCheck, label: 'GDPR Aligned' },
  { icon: KeyRound, label: 'ST4S Aligned' },
  { icon: Lock, label: '256-bit Encryption' }
] as const;

interface TrustBadgesProps {
  /** 'light' (default) for white/neutral sections, 'dark' for dark-bg sections */
  variant?: 'light' | 'dark';
  className?: string;
}

const badgeStyles: Record<
  'light' | 'dark',
  {
    color: string;
    border: string;
    backgroundColor: string;
  }
> = {
  light: {
    color: 'var(--mktg-gray)',
    border: '1px solid var(--mktg-card-border)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)'
  },
  dark: {
    color: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    backgroundColor: 'rgba(255, 255, 255, 0.06)'
  }
};

export function TrustBadges({
  variant = 'light',
  className
}: TrustBadgesProps) {
  const styles = badgeStyles[variant];

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-4',
        className
      )}
      role='list'
      aria-label='Security and compliance certifications'
    >
      {BADGES.map(({ icon: Icon, label }) => (
        <div
          key={label}
          role='listitem'
          className='flex items-center gap-2'
          style={{
            fontSize: '13px',
            fontFamily: 'var(--font-body)',
            fontWeight: 500,
            color: styles.color,
            border: styles.border,
            borderRadius: '8px',
            padding: '6px 12px',
            backgroundColor: styles.backgroundColor
          }}
        >
          <Icon style={{ width: 14, height: 14, opacity: 0.6 }} />
          {label}
        </div>
      ))}
    </div>
  );
}
